import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Snackbar,
    Alert
} from '@mui/material';
import {
    FamilyRestroom,
    ArrowBack,
    Save
} from '@mui/icons-material';
import baseurl from '../Baseurl/baseurl';
import { Card, Eyebrow, Button, FormField, Input, Textarea, Avatar, Loader, EmptyState } from '../ui';

const EditFamily = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [member, setMember] = useState(null);
    const [family, setFamily] = useState({
        father_name: '',
        father_contact: '',
        mother_name: '',
        mother_contact: '',
        spouse_name: '',
        spouse_contact: '',
        number_of_children: 0,
        children_names_text: '',
        address: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Fetch member data
    useEffect(() => {
        const fetchMember = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${baseurl}/api/member/${id}`);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.msg || 'Failed to fetch member');
                }

                setMember(data.data);

                // Initialize family data if exists
                if (data.data.MemberFamily) {
                    // Convert children names from JSON string to comma-separated text safely
                    let childrenText = '';
                    try {
                        const raw = data.data.MemberFamily.children_names;
                        if (raw && raw !== 'N/A') {
                            const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                            if (Array.isArray(arr)) {
                                childrenText = arr.join(', ');
                            }
                        }
                    } catch (_) {
                        childrenText = '';
                    }

                    setFamily({
                        father_name: data.data.MemberFamily.father_name && data.data.MemberFamily.father_name !== 'N/A' ? data.data.MemberFamily.father_name : '',
                        father_contact: data.data.MemberFamily.father_contact && data.data.MemberFamily.father_contact !== 'N/A' ? data.data.MemberFamily.father_contact : '',
                        mother_name: data.data.MemberFamily.mother_name && data.data.MemberFamily.mother_name !== 'N/A' ? data.data.MemberFamily.mother_name : '',
                        mother_contact: data.data.MemberFamily.mother_contact && data.data.MemberFamily.mother_contact !== 'N/A' ? data.data.MemberFamily.mother_contact : '',
                        spouse_name: data.data.MemberFamily.spouse_name && data.data.MemberFamily.spouse_name !== 'N/A' ? data.data.MemberFamily.spouse_name : '',
                        spouse_contact: data.data.MemberFamily.spouse_contact && data.data.MemberFamily.spouse_contact !== 'N/A' ? data.data.MemberFamily.spouse_contact : '',
                        number_of_children: data.data.MemberFamily.number_of_children || 0,
                        children_names_text: childrenText,
                        address: data.data.MemberFamily.address && data.data.MemberFamily.address !== 'N/A' ? data.data.MemberFamily.address : ''
                    });
                }
            } catch (err) {
                setError(err.message || 'An error occurred while fetching member');
                setSnackbarMessage(err.message || 'Failed to load family details');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                console.error('Error fetching member:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchMember();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFamily(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleMaritalStatusChange = (e) => {
        const maritalStatus = e.target.value;
        setMember(prev => ({
            ...prev,
            marital_status: maritalStatus
        }));

        // If marital status changes to single or disabled, clear spouse and children details
        if (['single', 'disabled'].includes((maritalStatus || '').toLowerCase())) {
            setFamily(prev => ({
                ...prev,
                spouse_name: '',
                spouse_contact: '',
                children_names_text: '',
                number_of_children: 0
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            // Convert children names text to array
            const childrenArray = family.children_names_text
                ? family.children_names_text.split(',').map(name => name.trim()).filter(Boolean)
                : [];

            // Prepare payload matching backend expectations
            const payload = {
                member_id: parseInt(id),
                father_name: family.father_name,
                father_contact: family.father_contact,
                mother_name: family.mother_name,
                mother_contact: family.mother_contact,
                spouse_name: family.spouse_name,
                spouse_contact: family.spouse_contact,
                number_of_children: Number.isFinite(Number(family.number_of_children)) ? Number(family.number_of_children) : childrenArray.length,
                children_names: childrenArray,
                address: family.address,
                marital_status: member?.marital_status || ''
            };

            const response = await fetch(`${baseurl}/api/family-details/update/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.msg || 'Failed to update family details');
            }

            setSnackbarMessage('Family details updated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Redirect after successful update
            setTimeout(() => {
                navigate('/admin/FamilyInformation');
            }, 1500);
        } catch (err) {
            setError(err.message || 'An error occurred while updating');
            setSnackbarMessage(err.message || 'Failed to update family details');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            console.error('Error updating family:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Determine if children details should be disabled
    const marital = (member?.marital_status || '').toLowerCase();
    const showSpouse = ['married', 'divorced', 'widowed'].includes(marital);
    const isChildDetailsDisabled = ['single', 'disabled'].includes(marital);

    if (loading && !member) {
        return <Loader label="Loading family details…" />;
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="mb-5 border-b border-line/10 pb-4">
                    <Eyebrow>Families</Eyebrow>
                    <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
                        Edit <em className="kbc-serif">Family</em>
                    </h1>
                </div>
                <EmptyState
                    title="Something went wrong"
                    description={error}
                    action={
                        <Button variant="ghost" leftIcon={<ArrowBack fontSize="small" />} onClick={() => navigate('/admin/FamilyInformation')}>
                            Back to Family Information
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page head */}
            <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <Eyebrow>Families</Eyebrow>
                    <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
                        Edit <em className="kbc-serif">Family</em> Details
                    </h1>
                    <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
                        Update household and relative information for this member.
                    </p>
                </div>
                <Button
                    variant="ghost"
                    leftIcon={<ArrowBack fontSize="small" />}
                    onClick={() => navigate('/admin/FamilyInformation')}
                >
                    Back
                </Button>
            </div>

            <Card>
                {/* Member identity */}
                <div className="mb-6 flex items-center gap-4 border-b border-line/12 pb-6">
                    <Avatar
                        src={member?.profile_image ? `${baseurl}/${member.profile_image}` : undefined}
                        name={`${member?.first_name || ''} ${member?.last_name || ''}`}
                        size="xl"
                    />
                    <div>
                        <h2 className="font-display text-xl font-semibold text-ink">
                            {member?.first_name} {member?.last_name}
                        </h2>
                        <p className="text-sm text-muted">Member profile</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Section heading */}
                    <div className="mb-5 flex items-center gap-2">
                        <FamilyRestroom className="text-primary" fontSize="small" />
                        <h3 className="font-display text-lg font-semibold text-ink">Family Information</h3>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <FormField label="Father's Name" htmlFor="father_name">
                            <Input
                                id="father_name"
                                name="father_name"
                                value={family.father_name}
                                onChange={handleChange}
                            />
                        </FormField>

                        <FormField label="Father's Contact" htmlFor="father_contact">
                            <Input
                                id="father_contact"
                                name="father_contact"
                                value={family.father_contact}
                                onChange={handleChange}
                            />
                        </FormField>

                        <FormField label="Mother's Name" htmlFor="mother_name">
                            <Input
                                id="mother_name"
                                name="mother_name"
                                value={family.mother_name}
                                onChange={handleChange}
                            />
                        </FormField>

                        <FormField label="Mother's Contact" htmlFor="mother_contact">
                            <Input
                                id="mother_contact"
                                name="mother_contact"
                                value={family.mother_contact}
                                onChange={handleChange}
                            />
                        </FormField>

                        <FormField label="Spouse Name" htmlFor="spouse_name">
                            <Input
                                id="spouse_name"
                                name="spouse_name"
                                value={family.spouse_name}
                                onChange={handleChange}
                                disabled={!showSpouse}
                            />
                        </FormField>

                        <FormField label="Spouse Contact" htmlFor="spouse_contact">
                            <Input
                                id="spouse_contact"
                                name="spouse_contact"
                                value={family.spouse_contact}
                                onChange={handleChange}
                                disabled={!showSpouse}
                            />
                        </FormField>

                        <FormField label="Marital Status" htmlFor="marital_status">
                            <Input
                                id="marital_status"
                                name="marital_status"
                                value={member?.marital_status || ''}
                                onChange={handleChange}
                                disabled
                            />
                        </FormField>

                        <FormField label="Family Address" htmlFor="address" className="md:col-span-2">
                            <Textarea
                                id="address"
                                name="address"
                                value={family.address}
                                onChange={handleChange}
                                rows={2}
                            />
                        </FormField>

                        <FormField label="Number of Children" htmlFor="number_of_children">
                            <Input
                                id="number_of_children"
                                type="number"
                                name="number_of_children"
                                value={family.number_of_children}
                                onChange={handleChange}
                                disabled={isChildDetailsDisabled}
                            />
                        </FormField>

                        <FormField label="Children Names (comma separated)" htmlFor="children_names_text">
                            <Input
                                id="children_names_text"
                                name="children_names_text"
                                value={family.children_names_text}
                                onChange={handleChange}
                                placeholder="e.g., John, Mary, David"
                                disabled={isChildDetailsDisabled}
                            />
                        </FormField>
                    </div>

                    {/* Submit Button */}
                    <div className="mt-7">
                        <Button
                            type="submit"
                            leftIcon={<Save fontSize="small" />}
                            loading={loading}
                            disabled={loading}
                        >
                            Save Changes
                        </Button>
                    </div>
                </form>
            </Card>

            {/* Success/Error Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditFamily;
